import { useField } from 'formik';
import { Box, Flex } from 'reflexbox';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Subheading, QuillInput, Magic } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import { SectionTitle } from 'packages/kakapo-ui-kit/components/common/Headings';
import Label from 'packages/kakapo-ui-kit/components/forms/controls/Label';
import Column from 'packages/kakapo-ui-kit/components/grid/Column';
import StarRating, {
	StarRatingElement,
} from 'packages/kakapo-ui-kit/components/forms/controls/StarRating';
import QuillText from 'packages/kakapo-ui-kit/components/editors/QuillText';

import FeedbackComposerGoalCard from '../composer/FeedbackComposerGoalCard';

import FeedbackRespondRequired from './FeedbackRespondRequired';
import { CompetencyLevelsInput } from '../../evaluation-submissions/input/CompetencyInput';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import VoiceMemoTarget from 'voice-memos/VoiceMemoTarget';
import GoalCard from 'goals/GoalCard';

const Answer = styled(Box)`
	border: 1px solid ${(props) => props.theme.colors.lightBlueDark};
	border-radius: 5px;
`;

export default function FeedbackResponseAnswer(props: {
	question: graphql.FeedbackRequestQuestion;
	answer?: graphql.FeedbackResponseAnswer;
	error?: boolean;
	name: string;
	index: number;
	viewerCanUpdate: boolean;
}) {
	const { localize } = useLocalize();
	const { t } = useTranslation();

	const [{ value: text }] = useField(`${props.name}.text`);
	const [{ value: voiceMemo }, , { setValue: setVoiceMemo }] = useField(`${props.name}.voiceMemo`);

	return (
		<Column pt={2} pb={3}>
			<Column>
				<SectionTitle>
					<Flex>
						<Box flexGrow={0} flexShrink={0} mr={3} width={32}>
							{props.index + 1}.
						</Box>
						<Box>{props.question.text}</Box>
					</Flex>
				</SectionTitle>
				{props.question.description && (
					<Box mt={3} ml="48px">
						<QuillText quill={props.question.description} />
					</Box>
				)}
			</Column>
			<Flex mt={3}>
				<Box flexGrow={0} flexShrink={0} mr={3} width={32} />
				<Box flexGrow={1}>
					{props.question.competency && (
						<Box mb={3}>
							<Subheading mb={2}>{localize(props.question.competency.title).text}</Subheading>
							<CompetencyLevelsInput
								party={graphql.EvaluationSubmissionParty.Manager}
								competency={props.question.competency}
								name={`${props.name}.competencyLevelIndex`}
								suffix=""
							/>
						</Box>
					)}
					{props.question.goal && (
						<Box mb={3}>
							<FeedbackComposerGoalCard goal={props.question.goal} />
						</Box>
					)}
					{props.question.goalRevision && (
						<Box mb={3}>
							<GoalCard goal={props.question.goalRevision} modal />
						</Box>
					)}
					{props.error && (
						<FeedbackRespondRequired>{t('feedback.answer.required')}</FeedbackRespondRequired>
					)}
					{(props.question.type === graphql.FeedbackQuestionType.Stars ||
						props.question.type === graphql.FeedbackQuestionType.Both) && (
						<Column pb={3}>
							{props.viewerCanUpdate && (
								<Label title={t('feedback.answer.howDidUserPerform')} color="lightBlueDark" />
							)}
							{props.viewerCanUpdate ? (
								<StarRating name={`${props.name}.stars`} />
							) : (
								<StarRatingElement
									value={props.answer?.stars ?? 0}
									name={`${props.name}.stars`}
									disabled
								/>
							)}
						</Column>
					)}

					{(props.question.type === graphql.FeedbackQuestionType.Text ||
						props.question.type === graphql.FeedbackQuestionType.Both) && (
						<Column pb={3}>
							{props.viewerCanUpdate && (
								<Label
									title={
										props.question.competency
											? t('feedback.answer.explainOptional')
											: t('feedback.answer.explain')
									}
									color="lightBlueDark"
								/>
							)}
							{props.viewerCanUpdate ? (
								<VoiceMemoTarget voiceMemo={voiceMemo} onChange={setVoiceMemo}>
									<Magic
										name="feedback.answer.suggestImprovements"
										context={{
											question: props.question.text,
											answer: text?.ops.map((op: any) => op.insert ?? '').join('') ?? '',
										}}
										button={t('feedback.answer.magic.button')}
									>
										<QuillInput.Formik name={`${props.name}.text`} />
									</Magic>
								</VoiceMemoTarget>
							) : (
								<Answer p={3}>
									<QuillText
										quill={props.answer?.text}
										placeholder={t('feedback.answer.emptyTextQuestionPlaceholder')}
									/>
								</Answer>
							)}
						</Column>
					)}
				</Box>
			</Flex>
		</Column>
	);
}
